import { Paths } from "@app/routes";
import { Card, CardContent, Skeleton } from "@atlas/core";
import { FilterBuilder, useTranslation } from "@atlas/sdk";
import type { IHubCatalog, IHubPackageDeployment } from "@models";
import { useSearchHubCatalogs } from "@query";
import { useCallback, useMemo } from "react";
import { createSearchParams, useNavigate } from "react-router";
import { CatalogItemCard } from "../../catalog/pages/catalog/CatalogItemCard";

export function ExpandedCatalogs({
  deployment,
}: {
  deployment: IHubPackageDeployment;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleMoreOptions = useCallback(
    (action: string, catalog?: IHubCatalog) => {
      if (action === "details" && catalog?.catalogId != null) {
        navigate({
          pathname: `/${Paths.CatalogManager}/${Paths.CatalogDetails}`,
          search: createSearchParams({
            catalogId: String(catalog.catalogId),
            version: String(catalog.version),
            fromTab: "deployments",
          }).toString(),
        });
      }
    },
    [navigate],
  );
  const catalogIds = useMemo(
    () =>
      (deployment.installedCatalogs ?? []).map((c) => ({
        catalogId: c.catalogId,
        version: c.catalogVersion,
      })),
    [deployment.installedCatalogs],
  );

  const filter = useMemo(() => {
    const builder = new FilterBuilder();
    if (catalogIds.length > 0) {
      builder.in(
        "catalogId",
        catalogIds.map((c) => c.catalogId),
      );
    } else {
      builder.equals("catalogId", -1);
    }
    return builder.build();
  }, [catalogIds]);

  const { data: catalogsData, isLoading } = useSearchHubCatalogs(
    {
      filter,
      mode: "DATA",
      startIndex: 0,
      limit: catalogIds.length || 10,
    },
    { enabled: catalogIds.length > 0 },
  );

  const catalogs = useMemo(
    () => catalogsData?.pages.flatMap((page) => page.items) ?? [],
    [catalogsData],
  );

  return (
    <Card className="m-4 bg-muted/20 border-none shadow-none">
      <CardContent className="p-4 flex flex-wrap gap-4">
        {isLoading ? (
          Array.from({ length: catalogIds.length || 1 }).map((_, i) => (
            <Skeleton key={i} className="h-[263px] w-[345px] rounded-xl" />
          ))
        ) : catalogs.length > 0 ? (
          catalogs.map((catalog) => (
            <CatalogItemCard
              key={`${catalog.catalogId}-${catalog.version}`}
              catalog={catalog}
              onMoreOptions={handleMoreOptions}
              showOwnerActionsMenu={false}
              showSubscribeAction={false}
              showLifecycleBadge={false}
            />
          ))
        ) : (
          <div className="text-muted-foreground text-sm">
            {t("hub.package_manager.no_catalogs_installed", {
              defaultValue: "No catalogs installed.",
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
